'use client';
import { useRef, useId, useCallback, useEffect } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Box, getColorByToken } from '@wanteddev/wds-engine';
import { hideOthers } from '../../utils/aria-hidden.mjs';
import RemoveScroll from '../remove-scroll/index.mjs';
import { Portal, FlexBox, DismissableLayer, Typography, TextButton } from '../index.mjs';
import FocusScope from '../focus-scope/index.mjs';
import { useDialogStore } from '../../stores/dialog-store.mjs';
import { dialogWrapperStyle, dialogDimmerStyle, dialogStyle, dialogContentStyle, dialogActionStyle } from './style.mjs';
import { jsx, jsxs } from 'react/jsx-runtime';

var Dialog = () => {
  const items = useDialogStore((state) => state.items);
  return /* @__PURE__ */ jsx(Portal, { children: items.map((dialog) => /* @__PURE__ */ jsx(Item, { ...dialog }, dialog.id)) });
};
var Item = ({
  id,
  content,
  title,
  confirmText,
  confirmColor = "palette.primary.normal",
  direction = "normal",
  cancelText,
  disableOutsideClickClose,
  disableEscapeKeyDownClose,
  resolve
}) => {
  const hide = useDialogStore((state) => state.hide);
  const ref = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  const handleClose = useCallback(() => {
    hide(id);
  }, [hide, id]);
  const handleCancel = useCallback(() => {
    handleClose();
    resolve("cancel");
  }, [handleClose, resolve]);
  const handleConfirm = useCallback(() => {
    handleClose();
    resolve("confirm");
  }, [handleClose, resolve]);
  useEffect(() => {
    const element = ref.current;
    if (element) {
      return hideOthers(element);
    }
  }, []);
  return /* @__PURE__ */ jsxs(FlexBox, { sx: dialogWrapperStyle, "wds-ignore-dismissable-layer": "true", children: [
    /* @__PURE__ */ jsx(RemoveScroll, { as: Slot, allowPinchZoom: true, shards: [ref], children: /* @__PURE__ */ jsx(
      Box,
      {
        sx: dialogDimmerStyle,
        onClick: () => {
          if (!disableOutsideClickClose) {
            handleCancel();
          }
        }
      }
    ) }),
    /* @__PURE__ */ jsx(FocusScope, { loop: true, trapped: true, children: /* @__PURE__ */ jsx(
      DismissableLayer,
      {
        onPointerDownOutside: (e) => {
          const originalEvent = e.detail.originalEvent;
          const ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
          const isRightClick = originalEvent.button === 2 || ctrlLeftClick;
          if (isRightClick || disableEscapeKeyDownClose)
            e.preventDefault();
        },
        onFocusOutside: (e) => e.preventDefault(),
        onDismiss: handleCancel,
        role: "presentation",
        asChild: true,
        children: /* @__PURE__ */ jsx(Box, { sx: dialogStyle, children: /* @__PURE__ */ jsxs(
          FlexBox,
          {
            ref,
            role: "alertdialog",
            "aria-describedby": descriptionId,
            "aria-labelledby": titleId,
            flexDirection: "column",
            sx: dialogContentStyle,
            children: [
              /* @__PURE__ */ jsxs(
                FlexBox,
                {
                  flexDirection: "column",
                  gap: "6px",
                  sx: { padding: "20px" },
                  children: [
                    Boolean(title) && /* @__PURE__ */ jsx(
                      Typography,
                      {
                        variant: "headline1",
                        weight: "bold",
                        color: "palette.label.normal",
                        children: title
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Typography,
                      {
                        variant: "body2_normal",
                        weight: "regular",
                        color: "palette.label.alternative",
                        sx: {
                          wordBreak: "keep-all",
                          overflowWrap: "anywhere"
                        },
                        display: "block",
                        children: content
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs(
                FlexBox,
                {
                  flexDirection: direction === "reverse" ? "row-reverse" : "row",
                  alignItems: "center",
                  justifyContent: direction === "reverse" ? "initial" : "flex-end",
                  gap: "24px",
                  sx: dialogActionStyle,
                  children: [
                    /* @__PURE__ */ jsx(CancelButton, { onClick: handleCancel, children: cancelText }),
                    /* @__PURE__ */ jsx(ConfirmButton, { onClick: handleConfirm, color: confirmColor, children: confirmText })
                  ]
                }
              )
            ]
          }
        ) })
      }
    ) })
  ] });
};
var ConfirmButton = ({ color, onClick, children }) => /* @__PURE__ */ jsx(
  TextButton,
  {
    size: "medium",
    variant: "primary",
    onClick,
    sx: (theme) => ({
      color: getColorByToken(theme, color),
      ['[wds-component="with-interaction"]']: {
        backgroundColor: getColorByToken(theme, color)
      }
    }),
    children
  }
);
var CancelButton = ({
  onClick,
  children
}) => {
  if (!children) {
    return null;
  }
  return /* @__PURE__ */ jsx(TextButton, { size: "medium", variant: "assistive", onClick, children });
};
var dialog_default = Dialog;

export { dialog_default as default };
